/**
 * The experimental surface's visual language, in one place.
 *
 * Deliberately shares nothing with the classic feed but the semantic colour tokens — those keep the
 * page themeable and keep the token lint honest. Everything else is different on purpose: no cards,
 * no borders around content, no severity washes. Structure comes from hairline rules and a fixed
 * number gutter; emphasis comes from the type scale; colour only ever means state.
 *
 * Metadata is set in the mono stack, uppercase, widely tracked. That one decision does most of the
 * work of telling this surface apart from the classic one, and it costs no font loading.
 */

/** The document measure. Claims and prose stay readable rather than spanning a wide viewport. */
export const MEASURE = 'max-w-[68ch]';
export const MEASURE_WIDE = 'max-w-[86ch]';

/** The gutter the entry number sits in, and the matching indent for anything beneath it. */
export const GUTTER = 'w-8 shrink-0';

export const TYPE = {
  /** The claim. The one thing a reader is here for, so it is the only large text on the page. */
  claim: 'text-xl leading-snug font-medium tracking-tight text-text-neutral-primary',
  /** A claim inside a detail view, where it is the page's title. */
  claimLead: 'text-2xl leading-snug font-medium tracking-tight text-text-neutral-primary',
  /** Supporting prose under a claim. */
  body: 'text-sm leading-relaxed text-text-neutral-secondary',
  /**
   * The apparatus behind a finding — what it rests on, what else could explain it, where the figures
   * came from. Set at the claim's own colour rather than the secondary grey `body` uses: a reader who
   * has opened the reasoning is reading it, not glancing past it, and grey small text is most of why
   * it read as unconsumable.
   */
  detail: 'text-sm leading-relaxed text-text-neutral-primary',
  /**
   * A note on the statement above it — the figure a claim turns on, and what that figure is not
   * precise about. A step below the prose it annotates in size and a step down in colour, because it
   * is read after the sentence or not at all. At prose size it was a second paragraph.
   */
  footnote: 'text-xs leading-relaxed text-text-neutral-secondary',
  /**
   * The name of a value in the apparatus — a description block's lead, a source's label. Sentence
   * case rather than the mono meta idiom: these sit in a column a reader scans down, and uppercase
   * tracking at that length is slower to read, not faster.
   */
  detailLabel: 'text-xs leading-relaxed text-text-neutral-secondary',
  /** The next action preview, and any single actionable line. */
  action: 'text-sm font-medium text-text-neutral-primary',
  /** Metadata: confidence, scope, counts. Mono, uppercase, tracked. */
  meta: 'font-mono text-[10px] uppercase tracking-[0.1em] text-text-neutral-secondary',
  /** A metadata value worth reading as a number. */
  metaValue: 'font-mono text-[10px] uppercase tracking-[0.1em] tabular-nums',
  /** Section headers — NEEDS ATTENTION, SOURCES, WHAT IT RESTS ON. */
  section: 'font-mono text-[11px] uppercase tracking-[0.14em] text-text-neutral-secondary',
  /** The entry number in the gutter. */
  ordinal: 'font-mono text-sm tabular-nums text-text-neutral-secondary',
  /** The run sentence in the header band. */
  sentence: 'text-xs leading-relaxed text-text-neutral-secondary',
} as const;

/**
 * Confidence is the one place colour carries meaning in the feed. Stated as text rather than as a
 * filled badge: a row of pills competes with the claim above it, which is the failure this replaces.
 */
export const CONFIDENCE_TONE = {
  High: 'text-text-success',
  Medium: 'text-text-warning',
  Unverified: 'text-text-error',
  Low: 'text-text-neutral-secondary',
} as const;

/**
 * Group tone — the same four the classic tab uses (`data/findingDisplay.ts` maps each group to a
 * severity, and these are those severities' text colours). A group has one tone across the product,
 * so a reader who has learned that blue means "about the data" does not have to learn it twice.
 *
 * Risk is the only group that reads as a problem; the others each say something different about why
 * they are not one.
 */
export const GROUP_TONE = {
  risk: 'text-text-warning',
  opportunity: 'text-text-success',
  ontrack: 'text-text-success',
  dataReadiness: 'text-text-info',
} as const;

export const GROUP_DOT = {
  risk: 'bg-bg-warning',
  opportunity: 'bg-bg-success',
  ontrack: 'bg-bg-success',
  dataReadiness: 'bg-bg-info',
} as const;

/** Section tone — and the audit's verdicts. */
export const TONE = {
  attention: 'text-text-warning',
  neutral: 'text-text-neutral-secondary',
  eliminated: 'text-text-success',
  open: 'text-text-error',
  untestable: 'text-text-neutral-secondary',
} as const;

/**
 * An objective's health, for the meta line's objective-scope entry — the same four
 * `ObjectiveHealthByIdsQuery` returns, painted with the product's own success/warning/error
 * vocabulary rather than a palette invented for this surface, so "Action Required" reads the same
 * red here as it does on the objective card.
 */
export const HEALTH_TONE = {
  HEALTHY: 'text-text-success',
  NEEDS_ATTENTION: 'text-text-warning',
  ACTION_REQUIRED: 'text-text-error',
  DONE: 'text-text-neutral-secondary',
} as const;

export const HEALTH_PROGRESS_CLASS = {
  HEALTHY: 'bg-bg-success-bold',
  NEEDS_ATTENTION: 'bg-bg-warning-bold',
  ACTION_REQUIRED: 'bg-bg-error-bold',
  DONE: 'bg-bg-neutral-bold',
} as const;

/**
 * The recommendation block, on a collapsed row and inside an expansion alike — one token, because
 * the whole point of the row's block is that it is the same line the expansion shows, and two class
 * strings would drift apart.
 *
 * It highlights under the pointer without being clickable: a reader talking through a run points at
 * a recommendation with the cursor, and the tint is what makes "this one" visible to whoever is
 * watching. Brand rather than neutral, so on a collapsed row it reads as its own thing lighting up
 * rather than as the row's own hover getting slightly darker.
 *
 * NEX-18039 tried giving an objective its own bordered panel ahead of this block (Direction B) and
 * considered boosting this token to a brand fill at rest to keep the recommendation the loudest thing
 * on the row. Dropped once the objective settled into an inline meta-line entry with no border of its
 * own (Direction D) — this is already the row's only bordered element, so nothing here needed to
 * change.
 */
export const RECOMMENDATION_SURFACE =
  'rounded-sm border border-border-neutral-subtle bg-bg-surface-base transition-colors hover:border-border-brand hover:bg-bg-brand-subtler';

/**
 * A row that can be selected or navigated into.
 *
 * `-subtle` and not `-subtle-hovered`, which is 10% ink: that pair is the rest/hover pair for a
 * surface already painted `-subtle` (6% → 10%, a +4pp change), and a row that is transparent at rest
 * takes the whole 10% as the change. Row hover across the product is the lighter step — `sui-table`
 * uses exactly this token — while 10% is what a button and a menu item hover at. The rows here were
 * hovering at button weight.
 */
export const ROW_INTERACTIVE =
  'w-full text-left transition-colors hover:bg-bg-neutral-subtle focus-visible:outline focus-visible:outline-2 focus-visible:outline-border-brand';
